import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  FileText, Star, Layout, Briefcase, Send, CheckCircle,
  Calendar, Zap, TrendingUp, Shield, ArrowRight,
} from "react-feather";
import { Navbar } from "./Navbar";
import Prices from "./Prices";
import Carousel from "./Carsoul";

/* The public landing page, the storefront. Every feature the product actually
   ships is on it, led by the differentiator (Auto-Apply's assisted send). All
   figures shown are real product facts, not marketing invention. */

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

// ── The full feature inventory, everything the product does today ──────────
// Exported: the logged-in home (Home1) renders the same list, one source of truth.
export const FEATURES = [
  {
    icon: FileText,
    title: "AI resume, tailored per job",
    desc: "Upload once. Our AI rewrites your resume against each job description, real experience only, never invented.",
  },
  {
    icon: Star,
    title: "Resume score & gap analysis",
    desc: "See exactly how your resume scores against a posting, which keywords are missing, and what to add to raise it.",
  },
  {
    icon: Layout,
    title: "4 recruiter-grade templates",
    desc: "Modern, Classic, Minimal, Executive, every one ATS-parseable and exported as a clean PDF.",
  },
  {
    icon: Briefcase,
    title: "A live job feed, ranked for you",
    desc: "Over a thousand live US postings from Greenhouse, Lever, and Ashby boards, matched and scored against your resume, and refreshed on demand.",
  },
  {
    icon: Send,
    title: "One-click assisted applications",
    desc: "Click Apply and a Chrome window opens with the employer's real form filled, your details, their questions answered from your resume, your tailored PDF attached.",
  },
  {
    icon: Shield,
    title: "You always press Submit",
    desc: "No bots pressing buttons in your name, no captcha tricks. The agent does the typing; the send is yours. That's why your applications land.",
  },
  {
    icon: CheckCircle,
    title: "Tracker with real confirmations",
    desc: "Every application recorded with the employer's own acknowledgement, a screenshot of the final page, and an email receipt.",
  },
  {
    icon: TrendingUp,
    title: "Interview-fit score per application",
    desc: "Each submission is scored against the JD, what matched, what's missing, so you walk into interviews knowing your angle.",
  },
  {
    icon: Calendar,
    title: "1:1 career coaching",
    desc: "Book a session with an advisor, resume review, interview practice, and strategy on a real calendar, not a chatbot.",
  },
];

const STEPS = [
  ["We rank the market for you", "More than a thousand live US postings are scored against your resume, and your best-fit roles float to the top. One click starts an application."],
  ["The agent fills everything", "A Chrome window opens on your screen and completes the employer's real form: contact details, screening questions, essays drafted in your own voice from your own history, resume attached."],
  ["You press Submit", "Review what was written, it's all visible, then send it yourself. We record the employer's confirmation and track it from there."],
];

function Home() {
  const navigate = useNavigate();

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    if (Cookies.get("access_token")) navigate("/home");
  }, [navigate]);

  const uploadPage = () => {
    if (Cookies.get("access_token")) navigate("/upload");
    else navigate("/signin", { state: { next: "/upload" } });
  };

  return (
    <>
      <Navbar />
      <div className="bg-white">

        {/* ── Hero ─────────────────────────────────────────────────────── */}
        <div className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-blue-50/50 to-white">
          {/* soft background blobs, subtle, not noise */}
          <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 rounded-full bg-blue-200/40 blur-3xl" />
          <div className="pointer-events-none absolute top-40 -left-32 w-80 h-80 rounded-full bg-sky-200/40 blur-3xl" />

          <div className="relative max-w-7xl mx-auto px-6 sm:px-10 pt-14 pb-20 sm:pt-20 sm:pb-24">
            <div className="grid lg:grid-cols-2 gap-14 items-center">
              {/* Left, the pitch */}
              <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
                <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-700 bg-blue-100/80 border border-blue-200 px-3 py-1 rounded-full">
                  <Zap className="w-3 h-3" /> AI resume builder + autonomous job applications
                </span>
                <h1 className="mt-5 text-4xl sm:text-5xl font-extrabold text-gray-900 leading-[1.1] tracking-tight">
                  From resume to <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-sky-500">submitted applications</span>, on autopilot.
                </h1>
                <p className="mt-5 text-lg text-gray-600 leading-relaxed max-w-xl">
                  Build an ATS-beating resume, then let Auto-Apply fill real employer
                  applications for you, every field, every question, answered from
                  your own history. <span className="font-semibold text-gray-800">You just press Submit.</span>
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-3">
                  <button
                    onClick={uploadPage}
                    className="inline-flex items-center gap-2 bg-blue-600 text-white font-bold px-6 py-3 rounded-xl hover:bg-blue-500 shadow-lg shadow-blue-600/20 transition"
                  >
                    Build my resume <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate("/auto-apply")}
                    className="inline-flex items-center gap-2 bg-white text-gray-800 font-semibold px-6 py-3 rounded-xl border border-gray-300 hover:border-blue-400 hover:text-blue-700 transition"
                  >
                    See Auto-Apply
                  </button>
                </div>
                <button
                  onClick={() => navigate("/express")}
                  className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-amber-600 hover:text-amber-500 transition"
                >
                  ⚡ In a hurry? Lightning Fast resume, no sign-up, done in minutes
                </button>

                {/* honest figures strip */}
                <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-6 max-w-xl">
                  {[["1,300+", "live US jobs, ranked to you"],
                    ["4", "recruiter-grade templates"],
                    ["$24.99", "per 1,000 applications"],
                    ["100%", "sent by you, never a bot"]].map(([n, l]) => (
                    <div key={l}>
                      <p className="text-2xl font-extrabold text-gray-900">{n}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug">{l}</p>
                    </div>
                  ))}
                </div>
              </motion.div>

              {/* Right, a product mock built in CSS, no stock art */}
              <motion.div
                className="relative hidden lg:block"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.15 }}
              >
                <div className="relative mx-auto max-w-md">
                  {/* application card */}
                  <div className="bg-white rounded-2xl shadow-2xl shadow-blue-900/10 border border-gray-100 p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {/* Fictional employer on purpose, no real company's mark in our mock */}
                        <span className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 font-bold flex items-center justify-center">N</span>
                        <div>
                          <p className="font-bold text-gray-900">Northbeam Logistics</p>
                          <p className="text-xs text-gray-500">Sales Development Representative</p>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-200 px-2.5 py-1 rounded-full">
                        <CheckCircle className="w-3 h-3" /> Confirmed
                      </span>
                    </div>
                    <div className="mt-4 space-y-2">
                      {[["Full name", "w-2/3"], ["Work authorization, Yes", "w-1/2"],
                        ["Why this role? (drafted in your voice)", "w-full"], ["Resume.pdf attached", "w-3/5"]].map(([label, w]) => (
                        <div key={label}>
                          <p className="text-[10px] text-gray-400 mb-1">{label}</p>
                          <div className={`h-2 rounded-full bg-gradient-to-r from-blue-200 to-blue-100 ${w}`} />
                        </div>
                      ))}
                    </div>
                    <div className="mt-5 flex items-center justify-between">
                      <p className="text-[11px] text-gray-400">12 fields auto-filled by the agent</p>
                      <span className="text-xs font-bold text-white bg-blue-600 px-4 py-1.5 rounded-lg ring-4 ring-blue-100">Submit</span>
                    </div>
                  </div>
                  {/* floating "your press" pill */}
                  <div className="absolute -top-5 -right-3 bg-white rounded-xl shadow-lg border border-gray-100 px-4 py-2">
                    <p className="text-xs font-semibold text-gray-800">👆 That click is yours</p>
                  </div>
                  {/* floating activity mini-card */}
                  <div className="absolute -bottom-9 -left-8 bg-white rounded-xl shadow-lg border border-gray-100 p-4">
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide mb-2">This week</p>
                    <div className="flex gap-1">
                      {[2, 4, 1, 3, 4, 2, 3].map((v, i) => (
                        <div key={i} className="w-3 rounded-sm bg-blue-500" style={{ height: 6 + v * 7, opacity: 0.35 + v * 0.16 }} />
                      ))}
                    </div>
                    <p className="text-[11px] font-semibold text-gray-700 mt-2">19 applications</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* ── Everything it does ───────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
          <motion.div className="text-center max-w-2xl mx-auto" {...fadeUp}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
              Everything between you and your next role
            </h2>
            <p className="mt-3 text-gray-600">
              One platform for the whole pipeline, resume, matching, applying, tracking, coaching.
            </p>
          </motion.div>

          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <motion.div
                key={title}
                className="group bg-white border border-gray-200 rounded-2xl p-6 hover:border-blue-300 hover:shadow-lg hover:shadow-blue-600/5 transition"
                {...fadeUp}
                transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              >
                <span className="inline-flex w-10 h-10 rounded-xl bg-blue-50 text-blue-600 items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition">
                  <Icon className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-bold text-gray-900">{title}</h3>
                <p className="mt-1.5 text-sm text-gray-600 leading-relaxed">{desc}</p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* ── Auto-Apply spotlight, the differentiator ────────────────── */}
        <div className="bg-gradient-to-br from-gray-900 via-blue-950 to-gray-900 text-white">
          <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20">
            <motion.div className="max-w-2xl" {...fadeUp}>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-300 bg-sky-400/10 border border-sky-400/30 px-3 py-1 rounded-full">
                <Zap className="w-3 h-3" /> Auto-Apply
              </span>
              <h2 className="mt-4 text-3xl sm:text-4xl font-extrabold tracking-tight">
                Applying is the grind.<br />We made it one click and one decision.
              </h2>
            </motion.div>

            <div className="mt-12 grid md:grid-cols-3 gap-6">
              {STEPS.map(([title, desc], i) => (
                <motion.div key={title} className="bg-white/5 border border-white/10 rounded-2xl p-6"
                  {...fadeUp} transition={{ duration: 0.5, delay: i * 0.1 }}>
                  <span className="text-sky-400 font-extrabold text-3xl">{i + 1}</span>
                  <h3 className="mt-3 font-bold text-lg">{title}</h3>
                  <p className="mt-2 text-sm text-blue-100/80 leading-relaxed">{desc}</p>
                </motion.div>
              ))}
            </div>

            <motion.div className="mt-10 flex flex-wrap items-center justify-between gap-4 bg-white/5 border border-white/10 rounded-2xl px-6 py-5" {...fadeUp}>
              <p className="text-sm text-blue-100/90">
                <Shield className="w-4 h-4 inline mr-1.5 -mt-0.5 text-sky-300" />
                Honest by design: answers come only from your real history, essays are always shown to you,
                and nothing is ever sent until <span className="font-semibold text-white">you</span> press the employer's own Submit button.
              </p>
              <button
                onClick={() => navigate("/auto-apply")}
                className="inline-flex items-center gap-2 bg-white text-gray-900 font-bold px-5 py-2.5 rounded-xl hover:bg-blue-50 transition flex-shrink-0"
              >
                Open Auto-Apply <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          </div>
        </div>

        {/* ── Social proof + pricing ───────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16">
          <Carousel />
        </div>
        <Prices embedded />

        {/* ── Final CTA ────────────────────────────────────────────────── */}
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pb-20">
          <motion.div className="bg-gradient-to-r from-blue-600 to-sky-500 rounded-3xl px-8 py-12 text-center text-white shadow-xl shadow-blue-600/20" {...fadeUp}>
            <h2 className="text-3xl font-extrabold tracking-tight">Your next role won't apply for itself.</h2>
            <p className="mt-2 text-blue-100">Actually, now it almost will.</p>
            <button
              onClick={uploadPage}
              className="mt-6 inline-flex items-center gap-2 bg-white text-blue-700 font-bold px-7 py-3 rounded-xl hover:bg-blue-50 transition"
            >
              Start with your resume <ArrowRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default Home;
